import React, { useState, useEffect, useMemo } from 'react';
import { fetchConfig } from './redux/actions/configActions';
import { useSelector, useDispatch } from 'react-redux';
import {
  Container,
  Card,
  Title,
  Group,
  Button,
  Table,
  Text,
  Badge,
  ActionIcon,
  Tooltip,
  Pagination,
  Loader,
  Center,
  Modal,
  Avatar,
  TextInput,
  Menu,
  Tabs,
  Stack,
  Box,
  ThemeIcon
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconCloudUpload,
  IconSearch,
  IconCloud,
  IconUser,
  IconList,
  IconDatabase
} from '@tabler/icons-react';
import { useAuth0 } from '@auth0/auth0-react';
import { setModelId, deleteModelAction } from './redux/actions/riskActions';
import { notifications } from '@mantine/notifications';
import ImportSageMakerModels from './utils/importSageMakerModels.js';
import ImportBedrockModels from './utils/importBedrockModels.js';
import ModelCardForm from './ModelCardForm';
import { RiskFormGenerator } from './redux/selectors/riskFormGenerator.js';

function ModelInventory() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  
  useEffect(() => {
    dispatch(fetchConfig('modelFormConfig'));
  }, [dispatch]);

  // Combine manual and cloud models into one array
  const manualModels = useSelector((state) => {
    const modelArray = Object.entries(state.risks.models).map(([model_uuid, model]) => ({
      ...model,
      model_uuid,
      origin: 'manual',
      source: 'Manual Entry'
    }));
    return modelArray;
  });

  const cloudModels = useSelector((state) => 
    (state.cloudModels.models || []).map(model => ({
      ...model,
      model_uuid: model.ModelCardName || model.modelName, // Use name as ID for cloud models
      modelName: model.ModelCardName || model.modelName,
      origin: 'imported',
      source: model.source || 'Unknown',
      created_at: model.CreationTime,
      assessmentStatus: 'Not Started',
      step: 'Not Started'
    }))
  );

  // Combined models array
  const allModels = useMemo(() => {
    const combined = [...manualModels, ...cloudModels];
    // Sort by created_at descending
    combined.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return combined;
  }, [manualModels, cloudModels]);

  const isLoading = useSelector((state) => state.risks.modelsLoading);
  const cloudModelsLoading = useSelector((state) => state.cloudModels.loading);
  const riskForm = useSelector(RiskFormGenerator);

  // State
  const [activePage, setActivePage] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [originFilter, setOriginFilter] = useState('all');
  const [openFormModal, setOpenFormModal] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [importMenuOpened, setImportMenuOpened] = useState(false);

  // Filtered models based on search and origin filter
  const filteredModels = useMemo(() => {
    return allModels.filter(model => {
      const matchesSearch = model.modelName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           model.modelType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           model.source?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesOrigin = originFilter === 'all' || model.origin === originFilter;
      return matchesSearch && matchesOrigin;
    });
  }, [allModels, searchTerm, originFilter]);

  // Paginated models
  const paginatedModels = useMemo(() => {
    const startIndex = (activePage - 1) * pageSize;
    return filteredModels.slice(startIndex, startIndex + pageSize);
  }, [filteredModels, activePage, pageSize]);

  const totalPages = Math.ceil(filteredModels.length / pageSize);

  const handleAddModel = () => {
    setEditingModel(null);
    setOpenFormModal(true);
  };

  const handleEditModel = (model) => {
    // Only allow editing manual models
    if (model.origin === 'manual') {
      setEditingModel(model);
      setOpenFormModal(true);
    } else {
      notifications.show({
        title: 'Cannot Edit',
        message: 'Imported models cannot be edited',
        color: 'yellow'
      });
    }
  };

  const handleDeleteClick = (model) => {
    if (model.origin === 'manual') {
      setModelToDelete(model);
      setDeleteModalOpen(true);
    } else {
      notifications.show({
        title: 'Cannot Delete',
        message: 'Imported models cannot be deleted',
        color: 'yellow'
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!modelToDelete) return;
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`/.netlify/functions/deleteModel?uuid=${modelToDelete.model_uuid}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      const result = await response.json();
      if (result.success) {
        dispatch(deleteModelAction(modelToDelete.model_uuid));
        notifications.show({
          title: 'Success',
          message: 'Model deleted successfully',
          color: 'green'
        });
      } else {
        console.error('Error deleting model:', result.error);
        notifications.show({
          title: 'Error',
          message: 'Failed to delete model',
          color: 'red'
        });
      }
    } catch (error) {
      console.error('Error deleting model:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to delete model',
        color: 'red'
      });
    }
    setDeleteModalOpen(false);
    setModelToDelete(null);
  };

  const handleFormSubmit = async (formData, formType, modelId) => {
    let response;
    setSavingModel(true);         // start spinner
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: 'auth.riskgpt.io', 
          scope: 'read:models write:models delete:models' 
        }
      });

      const body = { ...formData };
      if (formType === 'update' && modelId) {
        body.modelId = modelId;
      }

      // Save or update the model
      if (formType === 'create') {
        response = await fetch('/.netlify/functions/SaveModelCard', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(body),
        });
      } else if (formType === 'update' && modelId) {
        response = await fetch(`/.netlify/functions/SaveModelCard/${modelId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(body),
        });
      }

      const responseBody = await response.json();
      if (responseBody.success) {
        notifications.show({
          title: 'Success',
          message: responseBody.message,
          color: 'green'
        });

        const { data } = responseBody;

        console.log('data received from saving new model', data)

        if (formType === 'create' && data) {
          // New model created
          const { model_uuid, model_name,model_type, model_stage,use_case_short_description,created_at, created_by, avatar, createdByName } = data;

          // Attempt to fetch existing assessment
          let assessmentUuid;
          let assessmentStatus;
          let currentStep;
          const modelType=model_type;
          const modelStage=model_stage;
          const useCaseShortDescription=use_case_short_description;

          let fetchResponse = await fetch('/.netlify/functions/manageAssessment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ modelId: model_uuid, operation: 'fetch' }),
          });

          if (!fetchResponse.ok) {
            // If fetch fails, create a new assessment
            const createResponse = await fetch('/.netlify/functions/manageAssessment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                modelId: model_uuid,
                operation: 'update',
                currentStep: 'Not Started',
                assessmentStatus: 'Not Started',
                created_at,
                created_by,
                avatar,
                createdByName
              }),
            });

            if (!createResponse.ok) {
              throw new Error('Failed to create a new assessment for the new model.');
            }

            const createResult = await createResponse.json();
            assessmentUuid = createResult.assessmentUuid;
            assessmentStatus = 'Not Started';
            currentStep = 'Not Started';
          } else {
            // Fetch succeeded
            const result = await fetchResponse.json();
            assessmentUuid = result.assessmentUuid;
            currentStep = result.currentStep;
            assessmentStatus = result.assessmentStatus;
            
          }

          dispatch(setModelId(
            model_uuid,
            model_name,
            modelType,
            modelStage,
            useCaseShortDescription,
            created_at,
            created_by,
            avatar,
            createdByName,
            assessmentUuid,
            assessmentStatus,
            currentStep,
            riskForm));

        } else if (formType === 'update' && data) {
          // Model updated
          const { model_uuid, model_name, modelType, modelStage, useCaseShortDescription, created_at, created_by, avatar, createdByName, assessmentUuid, assessmentStatus, currentStep } = data;
          const existingRiskFormData = allModels.find(model => model.model_uuid === modelId)?.riskFormData || {};
         
          dispatch(setModelId(
            model_uuid,
            model_name,
            modelType,
            modelStage,
            useCaseShortDescription,
            created_at,
            created_by,
            avatar,
            createdByName,
            assessmentUuid || null, 
            assessmentStatus || null,
            currentStep || null,
            existingRiskFormData
            
          ));
        }

      } else {
        // Server returned success = false
        notifications.show({
          title: 'Error',
          message: responseBody.message,
          color: 'red'
        });
      }
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: `An error occurred: ${error.message}`,
        color: 'red'
      });
    }

    setOpenFormModal(false);   // close form modal after submit
    setEditingModel(null);
    setSavingModel(false);     // stop spinner
  };

  const toTitleCase = (str) => {
    str = str.replace(/\b(EU|ROC|AUC)\b/g, (match) => match.toUpperCase());
    return str
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/\b([Aa]nd)\b/g, '&')
      .split(/\s+/)
      .map((word) => {
        if (['EU', 'ROC', 'AUC'].includes(word)) {
          return word;
        } else {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
      })
      .join(' ')
      .trim();
  };

  const getOriginBadge = (model) => {
    if (model.origin === 'manual') {
      return <Badge variant="light" color="blue" size="sm" radius="sm">Manual</Badge>;
    } else {
      return <Badge variant="light" color="orange" size="sm" radius="sm">{model.source}</Badge>;
    }
  };

  const getStatusColor = (status) => {
    const normalizedStatus = (status || '').toLowerCase();
    if (normalizedStatus.includes('completed')) return 'green';
    if (normalizedStatus.includes('in progress') || normalizedStatus.includes('started')) return 'yellow';
    return 'gray';
  };

  if (isLoading) {
    return (
      <Container size="xl" px="md">
        <Center h={400}>
          <Stack align="center">
            <Loader size="lg" />
            <Text c="dimmed">Loading models...</Text>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="xl" px="md">
      <Stack spacing="xl">
        {/* Header */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" align="center" mb="xl">
            <Group align="flex-start" spacing="md">
              <ThemeIcon size={56} radius="md" variant="light" color="blue">
                <IconDatabase size={28} />
              </ThemeIcon>
              <Box>
                <Title order={1} size="h2" weight={700} mb={4}>
                  Model Inventory
                </Title>
                <Text size="md" c="dimmed">
                  Manage your AI models and cloud integrations
                </Text>
              </Box>
            </Group>
            
            <Group spacing="md">
              <Button 
                leftSection={<IconPlus size={16} />}
                onClick={handleAddModel}
                color="blue"
                size="md"
                radius="md"
              >
                Add Model
              </Button>
              
              <Menu 
                opened={importMenuOpened}
                onChange={setImportMenuOpened}
                shadow="md"
                width={200}
              >
                <Menu.Target>
                  <Button 
                    leftSection={<IconCloudUpload size={16} />}
                    variant="outline"
                    color="blue"
                    loading={isImporting}
                    size="md"
                    radius="md"
                  >
                    Import
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item>
                    <ImportSageMakerModels setIsImporting={setIsImporting} />
                  </Menu.Item>
                  <Menu.Item>
                    <ImportBedrockModels setIsImporting={setIsImporting} />
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
              
              {(isLoading || cloudModelsLoading) && <Loader size="sm" />}
            </Group>
          </Group>

          {/* Search and Filter */}
          <Group spacing="md" mb="xl">
            <TextInput
              placeholder="Search models..."
              leftSection={<IconSearch size={18} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1 }}
              size="md"
              radius="md"
            />
          </Group>
          
          {/* Tabs */}
          <Tabs 
            value={originFilter} 
            onChange={setOriginFilter}
            variant="pills"
            radius="md"
          >
            <Tabs.List>
              <Tabs.Tab 
                value="all" 
                leftSection={<IconList size={16} />}
              >
                All Models ({allModels.length})
              </Tabs.Tab>
              <Tabs.Tab 
                value="manual" 
                leftSection={<IconUser size={16} />}
              >
                Manual ({manualModels.length})
              </Tabs.Tab>
              <Tabs.Tab 
                value="imported" 
                leftSection={<IconCloud size={16} />}
              >
                Imported ({cloudModels.length})
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
        </Card>

        {/* Models Table */}
        <Card shadow="sm" padding={0} radius="md" withBorder>
          <Table highlightOnHover verticalSpacing="md" fontSize="sm">
            <thead style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
              <tr>
                <th style={{ padding: '16px 20px', fontWeight: 600, textAlign: 'left', minWidth: '200px' }}>Model Name</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Origin</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Type</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Stage</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Assessment Status</th>
                <th style={{ padding: '16px 12px', fontWeight: 600, textAlign: 'left' }}>Current Step</th>
                <th style={{ width: 120, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>Created By</th>
                <th style={{ width: 90, textAlign: 'center', padding: '16px 12px', fontWeight: 600 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedModels.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <Center py={60}>
                      <Stack align="center" spacing="md">
                        <Text size="lg" weight={500} c="dimmed">
                          No models found
                        </Text>
                        <Text size="sm" c="dimmed">
                          {searchTerm ? `No results for "${searchTerm}"` : 'No models available'}
                        </Text>
                      </Stack>
                    </Center>
                  </td>
                </tr>
              ) : (
                paginatedModels.map((model) => (
                  <tr key={model.model_uuid}>
                    <td style={{ padding: '16px 20px', textAlign: 'left' }}>
                      <Text size="sm" weight={600}>
                        {model.modelName}
                      </Text>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                      {getOriginBadge(model)}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                      <Text size="sm" c="dimmed">{model.modelType || '—'}</Text>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                      <Text size="sm" c="dimmed">{model.modelStage || '—'}</Text>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                      <Badge variant="light" color={getStatusColor(model.assessmentStatus)} size="sm" radius="sm">
                        {model.assessmentStatus}
                      </Badge>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'left' }}>
                      <Text size="sm" c="dimmed">{toTitleCase(model.step)}</Text>
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <Center>
                        <Tooltip label={model.createdByName || 'Unknown User'} withArrow>
                          <Avatar
                            src={model.avatar}
                            size={28}
                            radius="xl"
                            color="blue"
                          >
                            <IconUser size={16} />
                          </Avatar>
                        </Tooltip>
                      </Center>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
                    <Group spacing="xs" position="center" align="center" wrap="nowrap">
                      <Tooltip
                        label={model.origin === 'manual' ? 'Edit Model' : 'Cannot edit imported models'}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          size="lg"
                          radius="md"
                          onClick={() => handleEditModel(model)}
                          disabled={model.origin !== 'manual'}
                        >
                          <IconEdit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip
                        label={model.origin === 'manual' ? 'Delete Model' : 'Cannot delete imported models'}
                        withArrow
                      >
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="lg"
                          radius="md"
                          onClick={() => handleDeleteClick(model)}
                          disabled={model.origin !== 'manual'}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Group position="center" p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
              <Pagination
                value={activePage}
                onChange={setActivePage}
                total={totalPages}
                size="sm"
                radius="md"
              />
            </Group>
          )}
        </Card>
      </Stack>

      {/* Model Form Modal */}
      <Modal
        opened={openFormModal}
        onClose={() => setOpenFormModal(false)}
        title={editingModel ? 'Update Model Card' : 'Add Model Card'}
        size="lg"
        centered
        radius="md"
      >
        <ModelCardForm
          onSubmit={handleFormSubmit}
          modelId={editingModel?.model_uuid}
          initialData={editingModel || {}}
        />
        <Group position="right" mt="md">
          <Button
            variant="subtle"
            onClick={() => setOpenFormModal(false)}
            radius="md"
          >
            Cancel
          </Button>
          <Button
            color="teal"
            onClick={() => {
              const formEl = document.querySelector('form');
              if (formEl && !savingModel) {
                formEl.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
              }
            }}
            loading={savingModel}
            radius="md"
          >
            {editingModel ? 'Update' : 'Save'}
          </Button>
        </Group>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Confirm Delete"
        centered
        radius="md"
      >
        <Text mb="md">
          Are you sure you want to delete the model "{modelToDelete?.modelName}"?
        </Text>
        <Group position="right">
          <Button
            variant="subtle"
            onClick={() => setDeleteModalOpen(false)}
            radius="md"
          >
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDeleteConfirm}
            radius="md"
          >
            Confirm
          </Button>
        </Group>
      </Modal>
    </Container>
  );
}

export default ModelInventory;
